import { useState, useEffect } from 'react';
import {
  Layout,
  Typography,
  Row,
  Col,
  Card,
  Button,
  message,
  Spin,
  Slider,
  Segmented,
  Tabs,
  List,
  Divider,
  InputNumber,
  Tooltip,
  Modal,
} from 'antd';

import {
  CheckOutlined,
  CloseOutlined,
  MailOutlined,
  DatabaseOutlined,
  SafetyCertificateOutlined,
  CustomerServiceOutlined,
  LockOutlined,
  RocketOutlined,
} from '@ant-design/icons';

import Sidebar from '../components/Sidebar';
import AppHeader from '../components/Header';
import { decryptData } from '../middleware/encryption';

const { Content } = Layout;
const { Title, Text } = Typography;

/* ------------------------------------------------------
    CREDIT TYPES
------------------------------------------------------ */

const creditTypes = [
  {
    key: "emailFinder",
    label: "Email Credits",
    unitCost: 1,
    description: "1 credit"
  },
  {
    key: "mobileFinder",
    label: "Mobile Number Finder",
    unitCost: 5,
    description: "5 credits"
  },
  {
    key: "emailVerifier",
    label: "Email Verifier",
    unitCost: 1,
    description: "1 credit"
  },
  {
    key: "domainSearch",
    label: "Professional Email Finder",
    unitCost: 1,
    description: "1 credit"
  },
];

/* ------------------------------------------------------
    PRICING SLABS
------------------------------------------------------ */

const pricingSlabs = [
  {
    min: 1000,
    max: 9999,
    prices: {
      emailFinder: 0.01,
      mobileFinder: 0.06,
      emailVerifier: 0.01,
      domainSearch: 0.01
    }
  },
  {
    min: 10000,
    max: 99999,
    prices: {
      emailFinder: 0.009,
      mobileFinder: 0.055,
      emailVerifier: 0.009,
      domainSearch: 0.009
    }
  },
  {
    min: 100000,
    max: 999999,
    prices: {
      emailFinder: 0.006,
      mobileFinder: 0.05,
      emailVerifier: 0.006,
      domainSearch: 0.006
    }
  },
  {
    min: 1000000,
    max: Infinity,
    prices: {
      emailFinder: 0.0015,
      mobileFinder: 0.04,
      emailVerifier: 0.0015,
      domainSearch: 0.0015
    }
  }
];

const creditTooltips = {
  emailFinder: "Consumes 1 credit per email found",
  mobileFinder: "Consumes 5 credits per phone number found",
  emailVerifier: "Consumes 0.5 credits per email verification",
};

const getPricePerCredit = (typeKey, count) => {
  for (const slab of pricingSlabs) {
    if (count >= slab.min && count <= slab.max) {
      return slab.prices[typeKey];
    }
  }
  return 0.01;
};

/* ------------------------------------------------------
    SLIDER HELPERS
------------------------------------------------------ */

const sliderValues = [0, 1000, 10000, 100000, 1000000];

const valueToPosition = (val) => {
  if (val <= sliderValues[0]) return 0;
  if (val >= sliderValues[sliderValues.length - 1]) return 100;

  for (let i = 0; i < sliderValues.length - 1; i++) {
    const start = sliderValues[i];
    const end = sliderValues[i + 1];

    if (val <= end) {
      const segStart = i * 25;
      const segEnd = (i + 1) * 25;
      const ratio = (val - start) / (end - start);

      return segStart + ratio * (segEnd - segStart);
    }
  }

  return 100;
};

const positionToValue = (pos) => {
  if (pos <= 0) return sliderValues[0];
  if (pos >= 100) return sliderValues[sliderValues.length - 1];

  const i = Math.floor(pos / 25);

  const start = sliderValues[i];
  const end = sliderValues[i + 1];

  const segStart = i * 25;
  const segEnd = (i + 1) * 25;

  const ratio = (pos - segStart) / (segEnd - segStart);

  let value = start + ratio * (end - start);

  if (value > 1000) value = Math.round(value / 1000) * 1000;

  return Math.round(value);
};

/* ------------------------------------------------------
    COMPONENT
------------------------------------------------------ */

export default function PricingPage() {
  const [collapsed, setCollapsed] = useState(false);
  const [planTypes, setPlanTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [annualBilling, setAnnualBilling] = useState(false);
  const [tabKey, setTabKey] = useState('plans');
  const [subscribing, setSubscribing] = useState(false);
  const [subscribingPlanId, setSubscribingPlanId] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);

  const [creditCounts, setCreditCounts] = useState({
    emailFinder: 0,
    mobileFinder: 0,
    emailVerifier: 0,
    domainSearch: 0,
  });

  const planFeatures = [
  { key: "Email Finder", label: "Email Finder", creditKey: "emailFinder" },
  { key: "Mobile Finder", label: "Mobile Finder", creditKey: "mobileFinder" },
  { key: "Email Verifier", label: "Email Verifier", creditKey: "emailVerifier" },
  { key: "Domain Search", label: "Professional Email Finder" },
  { key: "API Access", label: "API Access" },
  { key: "Bulk Processing", label: "Bulk Processing" },
  { key: "Priority Support", label: "Priority Support" },
];

  /* ------------------------------------------------------
      FETCH PLANS
  ------------------------------------------------------ */

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const token = sessionStorage.getItem('access_token');

        const headers = new Headers();

        if (token) {
          headers.append('Authorization', `Bearer ${token}`);
        }

        const res = await fetch(
          'https://app.exellius.com/api/plans/getAllPlans',
          {
            method: 'GET',
            headers,
          }
        );

        const result = await res.json();

        if (result.data) {
          const decrypted = decryptData(result.data);

          const sortedPlans = decrypted.data.sort(
            (a, b) => a.points - b.points
          );

          setPlanTypes(sortedPlans);
        } else {
          message.error(result.message || 'Failed to fetch plans');
        }
      } catch (err) {
        message.error('An error occurred while fetching plans');
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  /* ------------------------------------------------------
      SUBSCRIBE
  ------------------------------------------------------ */

  const handleSubscribe = async (planId, isExtraCreditPlan = false) => {
    setSubscribing(true);
    setSubscribingPlanId(planId);

    try {
      const myHeaders = new Headers();

      const token = sessionStorage.getItem('access_token');

      if (token) {
        myHeaders.append('Authorization', `Bearer ${token}`);
      }

      myHeaders.append('Content-Type', 'application/json');

      let requestOptions;
      let url;

      if (isExtraCreditPlan) {
        const creditsPayload = Object.fromEntries(
          creditTypes.map((ct) => [ct.key, creditCounts[ct.key]])
        );

        const totalCredits = Object.values(creditCounts).reduce(
          (a, b) => a + b,
          0
        );

        const totalPrice = creditTypes
          .map((type) => {
            const price = getPricePerCredit(
              type.key,
              creditCounts[type.key]
            );

            return creditCounts[type.key] * price;
          })
          .reduce((a, b) => a + b, 0);

        requestOptions = {
          method: 'POST',
          headers: myHeaders,
          body: JSON.stringify({
            name: `Extra Credits Custom Plan`,
            description: `Custom credit bundle`,
            amount: totalPrice,
            credit_breakdown: creditsPayload,
            credits: totalCredits,
          }),
        };

        url =
          'https://app.exellius.com/api/plans/purchaseExtraCreditPlan';
      } else {
        const duration = annualBilling ? 'yearly' : 'monthly';

        requestOptions = {
          method: 'PUT',
          headers: myHeaders,
          body: JSON.stringify({
            planId,
            type: duration,
          }),
        };

        url =
          'https://app.exellius.com/api/plans/buySubscriptionPlan';
      }

      const response = await fetch(url, requestOptions);

      const result = await response.json();

      if (result.data) {
        const decryptedResponse = decryptData(result.data);

        window.location = decryptedResponse.url;

        message.success(
          decryptedResponse.message || 'Subscription successful'
        );
      } else {
        message.error(result.message || 'Subscription failed');
      }
    } catch (error) {
      message.error(
        'An error occurred while subscribing to the plan'
      );
    } finally {
      setSubscribing(false);
      setSubscribingPlanId(null);
    }
  };

  /* ------------------------------------------------------
      TOTALS
  ------------------------------------------------------ */

  const totalCredits = Object.values(creditCounts).reduce(
    (a, b) => a + b,
    0
  );

  const totalPrice = creditTypes
    .map((type) => {
      const price = getPricePerCredit(
        type.key,
        creditCounts[type.key]
      );

      return creditCounts[type.key] * price;
    })
    .reduce((a, b) => a + b, 0);

  /* ------------------------------------------------------
      FEATURE ROW
  ------------------------------------------------------ */

  const renderFeatureRow = (label, key, isHeader = false) => (
  <Row
    key={key}
    align="middle"
    style={{
      borderBottom: '1px solid #f3f0ff',
      minHeight: 58,
      background: isHeader ? '#faf7ff' : '#fff',
    }}
  >
    {/* Feature Name */}
    <Col
      xs={10}
      sm={10}
      md={8}
      lg={8}
      xl={8}
      style={{
        padding: '16px 20px',
        fontWeight: isHeader ? 700 : 500,
        color: '#1e1b4b',
      }}
    >
      {label}
    </Col>

    {/* Plans */}
    {planTypes.map((plan, idx) => (
      <Col
        key={plan.id}
        xs={14 / planTypes.length}
        sm={14 / planTypes.length}
        md={16 / planTypes.length}
        lg={16 / planTypes.length}
        xl={16 / planTypes.length}
        style={{
          textAlign: 'center',
          padding: 16,
          borderLeft: '1px solid #f3f0ff',
        }}
      >
        {isHeader ? (
          <div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: '#1e1b4b',
              }}
            >
              {plan.name}
            </div>

            <div
              style={{
                marginTop: 8,
                fontSize: 30,
                fontWeight: 800,
                color: '#7c3aed',
              }}
            >
              $
              {annualBilling
                ? Math.round(Number(plan.annual_price) / 12)
                : Number(plan.monthly_price)}

              <span
                style={{
                  fontSize: 14,
                  color: '#94a3b8',
                  fontWeight: 500,
                }}
              >
                /month
              </span>
            </div>

            <div
              style={{
                marginTop: 6,
                fontSize: 13,
                color: '#64748b',
              }}
            >
              Credits: <b>{plan.points?.toLocaleString()}</b>
            </div>

            <Button
              loading={subscribing && subscribingPlanId === plan.id}
              type={plan.name === 'Free' ? 'default' : 'primary'}
              style={{
                marginTop: 16,
                borderRadius: 10,
                width: '100%',
                height: 42,
                fontWeight: 600,
              }}
              onClick={() => handleSubscribe(plan.id)}
            >
              {plan.name === 'Free'
                ? 'Get Started'
                : 'Buy this plan'}
            </Button>
          </div>
        ) : (
          <>
            {plan.features.includes(key) ? (
              <CheckOutlined
                style={{
                  color: '#10b981',
                  fontSize: 18,
                  fontWeight: 700,
                }}
              />
            ) : (
              <CloseOutlined
                style={{
                  color: '#cbd5e1',
                  fontSize: 18,
                }}
              />
            )}
          </>
        )}
      </Col>
    ))}
  </Row>
);

  /* ------------------------------------------------------
      TABS
  ------------------------------------------------------ */

  const items = [
    {
      key: 'plans',

      label: (
        <div
  style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    paddingTop: 20,
    paddingBottom: 20,
    width: '100%',
    textAlign: 'left',
  }}
>
  <div
    style={{
      width: 58,
      height: 58,
      borderRadius: 16,
      background: 'linear-gradient(135deg, #f3edff 0%, #ede9fe 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#7c3aed',
      fontSize: 26,
      boxShadow: '0 10px 25px rgba(124,58,237,0.12)',
      flexShrink: 0,
    }}
  >
    <MailOutlined />
  </div>

  <div>
    <div
      style={{
        fontSize: 12,
        color: '#8c8c8c',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 1,
        marginBottom: 6,
      }}
    >
      All-in-one outreach platform
    </div>

    <div
      style={{
        fontSize: 28,
        fontWeight: 800,
        color: '#1e1b4b',
        lineHeight: 1.3,
      }}
    >
      Find, verify and send cold emails
    </div>
  </div>
</div>
      ),

      children: (
        <div
          style={{
            background: '#fff',
            borderRadius: 20,
            overflow: 'hidden',
            border: '1px solid #f1f1f1',
          }}
        >
          {/* TOP PRICING SECTION */}

         <div
  style={{
    border: '1px solid #f1ebff',
    borderRadius: 18,
    overflow: 'hidden',
    background: '#fff',
  }}
>
  {/* TOP HEADER */}
  <Row
    align="middle"
    style={{
      borderBottom: '1px solid #f3f0ff',
      background: '#faf7ff',
    }}
  >
    {/* LEFT INFO */}
    <Col span={8} style={{ padding: 24 }}>
      <Segmented
        value={annualBilling}
        onChange={setAnnualBilling}
        options={[
          { label: 'Monthly', value: false },
          { label: 'Annually 25% OFF', value: true },
        ]}
        style={{ marginBottom: 18 }}
      />

     <div
  style={{
    display: 'flex',
    alignItems: 'flex-start',
    gap: 16,
  }}
>
  {/* ICON */}
  <div
    style={{
      width: 58,
      height: 58,
      borderRadius: 18,
      background: 'linear-gradient(135deg, #f3e8ff 0%, #ede9fe 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#7c3aed',
      fontSize: 28,
      flexShrink: 0,
      boxShadow: '0 8px 20px rgba(124,58,237,0.12)',
    }}
  >
     ∞
  </div>

  {/* CONTENT */}
  <div>
    <div
      style={{
        fontSize: 20,
        fontWeight: 700,
        color: '#1e1b4b',
        marginBottom: 8,
      }}
    >
      Unlimited seats
    </div>

    <div
      style={{
        fontSize: 14,
        color: '#64748b',
        lineHeight: 1.8,
        maxWidth: 220,
      }}
    >
      Upgrade or downgrade Any time
    </div>
  </div>
</div>
    </Col>

    {/* PLAN CARDS */}
    {planTypes.map((plan) => (
      <Col
        span={4}
        key={plan.id}
        style={{
          padding: 24,
          borderLeft: '1px solid #f3f0ff',
          minHeight: 260,
        }}
      >
        <div
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: '#1e1b4b',
          }}
        >
          {plan.name}
        </div>

        <div
          style={{
            marginTop: 16,
            fontSize: 34,
            fontWeight: 800,
            color: '#7c3aed',
          }}
        >
          $
          {annualBilling
            ? Math.round(Number(plan.annual_price) / 12)
            : Number(plan.monthly_price)}

          <span
            style={{
              fontSize: 14,
              color: '#94a3b8',
              fontWeight: 500,
            }}
          >
            /month
          </span>
        </div>

        <div
          style={{
            marginTop: 10,
            fontSize: 14,
            color: '#64748b',
          }}
        >
          Credits: <b>{plan.points?.toLocaleString()}</b>
        </div>

        <Button
          loading={
            subscribing &&
            subscribingPlanId === plan.id
          }
          type={
            plan.name === 'Free'
              ? 'default'
              : 'primary'
          }
          style={{
            marginTop: 22,
            width: '100%',
            height: 44,
            borderRadius: 10,
            fontWeight: 600,
          }}
          onClick={() => handleSubscribe(plan.id)}
        >
          {plan.name === 'Free'
            ? 'Get Started'
            : 'Buy this plan'}
        </Button>
      </Col>
    ))}
  </Row>

  {/* FEATURES TABLE */}
  {planFeatures.map((feature) => (
    <Row
      key={feature.key}
      align="middle"
      style={{
        borderBottom: '1px solid #f3f0ff',
        minHeight: 58,
      }}
    >
      <Col
  span={8}
  style={{
    padding: '16px 24px',
    fontWeight: 500,
    color: '#1e1b4b',
  }}
>
  {creditTooltips[feature.creditKey] ? (
    <Tooltip title={creditTooltips[feature.creditKey]}>
      <span style={{ cursor: 'pointer', borderBottom: '1px dashed #94a3b8' }}>
        {feature.label}
      </span>
    </Tooltip>
  ) : (
    feature.label
  )}
</Col>

      {planTypes.map((plan) => (
        <Col
          span={4}
          key={plan.id}
          style={{
            textAlign: 'center',
            borderLeft: '1px solid #f3f0ff',
          }}
        >
          {plan.features.includes(feature.key) ? (
            <CheckOutlined
              style={{
                color: '#10b981',
                fontSize: 18,
              }}
            />
          ) : (
            <CloseOutlined
              style={{
                color: '#cbd5e1',
                fontSize: 18,
              }}
            />
          )}
        </Col>
      ))}
    </Row>
  ))}
</div>

          {/* FEATURE TABLE */}

          

          {/* FOOTER */}

          <div
            style={{
              padding: 24,
              borderTop: '1px solid #f3f1ff',
            }}
          >
            <Row
  gutter={[24, 24]}
  justify="center"
  align="middle"
  style={{
    marginTop: 10,
  }}
>
  {/* CUSTOM PLAN CARD */}
  <Col xs={24} md={12} lg={6}>
    <Card
      bordered={false}
      style={{
        background: 'linear-gradient(135deg, #faf7ff 0%, #f5f3ff 100%)',
        borderRadius: 20,
        height: '100%',
        boxShadow: '0 10px 30px rgba(124,58,237,0.08)',
      }}
      bodyStyle={{
        padding: 28,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
        }}
      >
        <div
          style={{
            width: 58,
            height: 58,
            borderRadius: 16,
            background: '#ede9fe',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#7c3aed',
            fontSize: 26,
            flexShrink: 0,
          }}
        >
          <CustomerServiceOutlined />
        </div>

        <div>
          <Title
            level={5}
            style={{
              margin: 0,
              color: '#1e1b4b',
              fontWeight: 700,
            }}
          >
            Need a custom plan?
          </Title>

          <Text
            type="secondary"
            style={{
              lineHeight: 1.7,
              display: 'block',
              marginTop: 6,
            }}
          >
            Get a personalized plan tailored
            to your business needs.
          </Text>
        </div>
      </div>

      <Button
        style={{
          marginTop: 24,
          borderRadius: 12,
          height: 44,
          fontWeight: 600,
          width: '100%',
        }}
        type="primary"
        href="mailto:support@exellius.com"
      >
        Contact Sales
      </Button>
    </Card>
  </Col>

  {/* FEATURE ITEMS */}
  {[
    {
      icon: <SafetyCertificateOutlined />,
      title: '30-Day Money Back',
      desc: 'Get a full refund within 30 days.',
    },
    {
      icon: <LockOutlined />,
      title: 'Secure & Reliable',
      desc: 'Enterprise-grade security you can trust.',
    },
    {
      icon: <CustomerServiceOutlined />,
      title: '24/7 Support',
      desc: 'We’re here anytime you need us.',
    },
  ].map((item, i) => (
    <Col xs={24} md={12} lg={6} key={i}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 18,
          padding: 24,
          borderRadius: 20,
          background: '#fff',
          height: '100%',
          boxShadow: '0 8px 25px rgba(15,23,42,0.05)',
        }}
      >
        <div
          style={{
            width: 58,
            height: 58,
            borderRadius: 16,
            background: 'linear-gradient(135deg, #f3edff 0%, #ede9fe 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#7c3aed',
            fontSize: 24,
            flexShrink: 0,
          }}
        >
          {item.icon}
        </div>

        <div>
          <div
            style={{
              fontWeight: 700,
              fontSize: 16,
              color: '#1e1b4b',
              marginBottom: 6,
            }}
          >
            {item.title}
          </div>

          <Text
            type="secondary"
            style={{
              lineHeight: 1.7,
              fontSize: 14,
            }}
          >
            {item.desc}
          </Text>
        </div>
      </div>
    </Col>
  ))}
</Row>
          </div>
        </div>
      ),
    },

    /* ------------------------------------------------------
        DATA PLATFORM
    ------------------------------------------------------ */

    {
      key: 'credits',

      label: (
        <div
  style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    paddingTop: 20,
    paddingBottom: 20,
    width: '100%',
    textAlign: 'left',
  }}
>
  <div
    style={{
      width: 58,
      height: 58,
      borderRadius: 16,
      background: 'linear-gradient(135deg, #f3edff 0%, #ede9fe 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#7c3aed',
      fontSize: 26,
      boxShadow: '0 10px 25px rgba(124,58,237,0.12)',
      flexShrink: 0,
    }}
  >
    <DatabaseOutlined />
  </div>

  <div>
    <div
      style={{
        fontSize: 12,
        color: '#8c8c8c',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 1,
        marginBottom: 6,
      }}
    >
      Data Platform
    </div>

    <div
      style={{
        fontSize: 28,
        fontWeight: 800,
        color: '#1e1b4b',
        lineHeight: 1.3,
      }}
    >
      Data solutions for your application
    </div>
  </div>
</div>
      ),

      children: (
        <div
          style={{
            background: '#fff',
            borderRadius: 20,
            padding: 28,
            border: '1px solid #f1f1f1',
          }}
        >
          <Row gutter={[30, 30]}>
            {/* LEFT */}

            <Col xs={24} md={15}>
              {creditTypes.map((type) => {
                const handleChange = (val) => {
                  setCreditCounts((prev) => ({
                    ...prev,
                    [type.key]: val,
                  }));
                };

                return (
                  <div
                    key={type.key}
                    style={{
                      marginBottom: 42,
                    }}
                  >
                    <div
                      style={{
                        marginBottom: 14,
                      }}
                    >
                      <Text
                        strong
                        style={{
                          fontSize: 16,
                        }}
                      >
                        {type.label}
                      </Text>

                      <Text
                        type="secondary"
                        style={{
                          marginLeft: 10,
                        }}
                      >
                        {type.description}
                      </Text>
                    </div>

                    <Row
                      gutter={20}
                      align="middle"
                    >
                      <Col flex="auto">
                        <Slider
                          min={0}
                          max={100}
                          value={valueToPosition(
                            creditCounts[type.key]
                          )}
                          onChange={(pos) =>
                            handleChange(
                              positionToValue(pos)
                            )
                          }
                          marks={{
                            0: '0',
                            25: '1K',
                            50: '10K',
                            75: '100K',
                            100: '1M',
                          }}
                        />
                      </Col>

                      <Col>
                        <InputNumber
                          style={{
                            width: 130,
                            borderRadius: 10,
                          }}
                          value={creditCounts[type.key]}
                          min={1000}
                          max={1000000}
                          onChange={handleChange}
                        />
                      </Col>
                    </Row>
                  </div>
                );
              })}
            </Col>

            {/* RIGHT */}

            <Col xs={24} md={9}>
              <Card
                bordered={false}
                style={{
                  borderRadius: 18,
                  background:
                    'linear-gradient(135deg,#faf7ff 0%,#ffffff 100%)',
                  boxShadow:
                    '0 10px 40px rgba(124,58,237,0.08)',
                }}
              >
                <Text type="secondary">
                  Summary
                </Text>

                <Title
                  level={2}
                  style={{
                    color: '#7c3aed',
                    marginTop: 10,
                    marginBottom: 20,
                  }}
                >
                  $
                  {totalPrice.toLocaleString(
                    undefined,
                    {
                      minimumFractionDigits: 2,
                    }
                  )}
                </Title>

                <Divider />

                <List
                  dataSource={creditTypes}
                  renderItem={(type) => {
                    const price = getPricePerCredit(
                      type.key,
                      creditCounts[type.key]
                    );

                    const subtotal =
                      creditCounts[type.key] * price;

                    return (
                      <List.Item>
                        <div
                          style={{
                            width: '100%',
                            display: 'flex',
                            justifyContent:
                              'space-between',
                          }}
                        >
                          <span>{type.label}</span>

                          <b>
                            $
                            {subtotal.toLocaleString(
                              undefined,
                              {
                                minimumFractionDigits: 2,
                              }
                            )}
                          </b>
                        </div>
                      </List.Item>
                    );
                  }}
                />

                <Divider />

                <div
                  style={{
                    marginBottom: 20,
                    lineHeight: 2,
                  }}
                >
                  <div>
                    <b>Total Credits:</b>{' '}
                    {totalCredits.toLocaleString()}
                  </div>

                  <div>
                    <b>Total Price:</b> $
                    {totalPrice.toLocaleString(
                      undefined,
                      {
                        minimumFractionDigits: 2,
                      }
                    )}
                  </div>
                </div>

                <Button
                  loading={
                    subscribing &&
                    subscribingPlanId === null
                  }
                  type="primary"
                  size="large"
                  icon={<RocketOutlined />}
                  style={{
                    width: '100%',
                    height: 48,
                    borderRadius: 12,
                    background: '#7c3aed',
                    fontWeight: 600,
                  }}
                  onClick={() =>
                    handleSubscribe(null, true)
                  }
                >
                  Buy Now
                </Button>
              </Card>
            </Col>
          </Row>
        </div>
      ),
    },
  ];

  /* ------------------------------------------------------
      RETURN
  ------------------------------------------------------ */

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sidebar
        collapsed={collapsed}
        onCollapse={setCollapsed}
      />

      <Layout>
        <AppHeader />

        <Content
          style={{
            padding: 24,
            background: '#fafafa',
            height: 'calc(100vh - 64px)',
            overflowY: 'auto',
          }}
        >
          <div
            style={{
              maxWidth: 1500,
              margin: '0 auto',
            }}
          >
            <Title
              level={2}
              style={{
                marginBottom: 4,
                color: '#1e1b4b',
              }}
            >
              Subscription & Credits
            </Title>

            <Text
              type="secondary"
              style={{
                fontSize: 15,
              }}
            >
              Choose the perfect plan to find, verify and
              send cold emails with ease.
            </Text>

            <div
              style={{
                marginTop: 28,
              }}
            >
              {loading ? (
                <Spin
                  size="large"
                  style={{
                    display: 'block',
                    marginTop: 120,
                  }}
                />
              ) : (
                <Tabs
                  items={items}
                  activeKey={tabKey}
                  onChange={setTabKey}
                  size="large"
                />
              )}
            </div>
          </div>
        </Content>
      </Layout>
    </Layout>
  );
}